// Cargos (nomina.cargos). Nómina → Personal.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, Switch, fieldCls, money, nom, useDialogoEnLinea, type Cargo } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const vacio = { codigo: '', nombre: '', descripcion: '', sueldo_referencial: '', es_activo: true };

export function Cargos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Cargo[]>({
    queryKey: ['nom-cargos'],
    queryFn: () => nom.list<Cargo>('cargos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (c?: Cargo) => {
    if (c) setForm({ codigo: c.codigo ?? '', nombre: c.nombre, descripcion: c.descripcion ?? '', sueldo_referencial: c.sueldo_referencial != null ? String(c.sueldo_referencial) : '', es_activo: c.es_activo });
    else setForm(vacio);
    setEditId(c?.id ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, descripcion: form.descripcion || null, sueldo_referencial: form.sueldo_referencial ? Number(form.sueldo_referencial) : null, es_activo: form.es_activo };
      return editId ? nom.update('cargos', editId, body) : nom.create('cargos', body);
    },
    onSuccess: () => { toast.success('Cargo guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-cargos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nom.update('cargos', id, { deleted_at: new Date().toISOString(), es_activo: false }),
    onSuccess: () => { toast.success('Cargo eliminado'); void qc.invalidateQueries({ queryKey: ['nom-cargos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Código', 'Nombre', 'Descripción', 'Sueldo ref.', 'Estado'];
  const MONEYCOLS = [4];
  const filasExport: Celda[][] = [
    ...rows.map((c, i): Celda[] => [
      i + 1, c.codigo, c.nombre, c.descripcion ?? '',
      c.sueldo_referencial != null ? Number(c.sueldo_referencial) : '', c.es_activo ? 'Activo' : 'Inactivo',
    ]),
    ['', '', '', 'TOTAL', rows.reduce((s, c) => s + (c.sueldo_referencial != null ? Number(c.sueldo_referencial) : 0), 0), ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cargos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`cargos-${fechaLocal()}`}
            titulo="Cargos" headers={headersExp} filas={filasExport} colsDerecha={[0, 4]}
            disabled={rows.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo cargo</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cargos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3 text-right">Sueldo ref.</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin cargos</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.codigo}</td>
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.descripcion}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.sueldo_referencial)}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar cargo' : 'Nuevo cargo'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="CAR-25" /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Campo label="Sueldo referencial ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={form.sueldo_referencial} onChange={(e) => setForm({ ...form, sueldo_referencial: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
